import apiClient, { setAuthToken } from '../infrastructure/api/apiClient';
import type { LoginResponse, User } from '../types/auth';

/**
 * 認証関連のAPIサービス
 */

class AuthService {
  private readonly TOKEN_KEY = 'mederi_auth_token';
  private readonly USER_KEY = 'mederi_user';

  /**
   * ログイン処理
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/api/login', {
        email,
        password,
      });

      if (response.data.success && response.data.token && response.data.user) {
        setAuthToken(response.data.token);
        localStorage.setItem(this.USER_KEY, JSON.stringify(response.data.user));
        return response.data;
      }

      throw new Error(response.data.error || 'ログインに失敗しました');
    } catch (error: unknown) {
      const axiosError = error as { response?: { status: number }; message?: string };

      if (axiosError.response?.status === 422) {
        throw new Error('メールアドレスまたはパスワードが正しくありません');
      }

      if (axiosError.response?.status === 401) {
        throw new Error('認証に失敗しました。メールアドレスとパスワードを確認してください');
      }

      throw new Error(axiosError.message || 'ログイン処理中にエラーが発生しました');
    }
  }

  /**
   * ログアウト処理
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/logout');
    } catch (error) {
      // APIエラーが発生してもローカルのクリーンアップは実行
    } finally {
      setAuthToken(null);
      localStorage.removeItem(this.USER_KEY);
    }
  }

  /**
   * 現在のユーザー情報を取得
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const storedUser = localStorage.getItem(this.USER_KEY);
      if (!storedUser) {
        return null;
      }

      const token = localStorage.getItem(this.TOKEN_KEY);
      if (!token) {
        localStorage.removeItem(this.USER_KEY);
        return null;
      }

      const response = await apiClient.get<{ success: boolean; data: User }>('/api/auth/me');

      if (response.data.success && response.data.data) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(response.data.data));
        return response.data.data;
      }

      return null;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status: number } };

      if (axiosError.response?.status === 401) {
        this.clearAuth();
        return null;
      }

      const storedUser = localStorage.getItem(this.USER_KEY);
      return storedUser ? JSON.parse(storedUser) : null;
    }
  }

  /**
   * 認証状態を確認
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const user = localStorage.getItem(this.USER_KEY);
    return !!(token && user);
  }

  /**
   * 保存されているユーザー情報を取得（API呼び出しなし）
   */
  getStoredUser(): User | null {
    try {
      const storedUser = localStorage.getItem(this.USER_KEY);
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * 認証情報をクリア（ローカルのみ）
   */
  private clearAuth(): void {
    setAuthToken(null);
    localStorage.removeItem(this.USER_KEY);
  }
}

export const authService = new AuthService();

export const login = authService.login.bind(authService);
export const logout = authService.logout.bind(authService);
export const getCurrentUser = authService.getCurrentUser.bind(authService);
export const isAuthenticated = authService.isAuthenticated.bind(authService);
export const getStoredUser = authService.getStoredUser.bind(authService);

export default authService;
